import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';
import { DeviceCategory } from '../../../DiscoveryEngine.TS/Core';


@Component({
	selector: 'hudu-default-settings',
	templateUrl: './hudu-default-settings.component.html',
	styleUrls: ['./hudu-default-settings.component.scss'],
})
export class HuduDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public apiKey: UntypedFormControl;

	public defaultLayout: UntypedFormControl;

	public firewallLayout: UntypedFormControl;
	public routerLayout: UntypedFormControl;
	public switchLayout: UntypedFormControl;
	public wlanControllerLayout: UntypedFormControl;
	public wirelessApLayout: UntypedFormControl;
	public printerLayout: UntypedFormControl;

	public virtualHostLayout: UntypedFormControl;
	public virtualMachineLayout: UntypedFormControl;
	public windowsServerLayout: UntypedFormControl;
	public windowsLayout: UntypedFormControl;
	public appleLayout: UntypedFormControl;
	public ipPhoneManagerLayout: UntypedFormControl;
	public ipPhoneLayout: UntypedFormControl;

	private apiKeyChanged: boolean;
	private apiKeyChangedSub: Subscription;

	private layoutChanged: boolean;

	private defaultLayoutChangedSub: Subscription;

	private firewallLayoutChangedSub: Subscription;
	private routerLayoutChangedSub: Subscription;
	private switchLayoutChangedSub: Subscription;
	private wlanControllerLayoutChangedSub: Subscription;
	private wirelessApLayoutChangedSub: Subscription;
	private printerLayoutChangedSub: Subscription;

	private virtualHostLayoutChangedSub: Subscription;
	private virtualMachineLayoutChangedSub: Subscription;
	private windowsServerLayoutChangedSub: Subscription;
	private windowsLayoutChangedSub: Subscription;
	private appleLayoutChangedSub: Subscription;
	private ipPhoneManagerLayoutChangedSub: Subscription;
	private ipPhoneLayoutChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.apiKeyChangedSub) {
			this.apiKeyChangedSub.unsubscribe();
		}

		if (this.defaultLayoutChangedSub) {
			this.defaultLayoutChangedSub.unsubscribe();
		}

		if (this.firewallLayoutChangedSub) {
			this.firewallLayoutChangedSub.unsubscribe();
		}
		if (this.routerLayoutChangedSub) {
			this.routerLayoutChangedSub.unsubscribe();
		}
		if (this.switchLayoutChangedSub) {
			this.switchLayoutChangedSub.unsubscribe();
		}
		if (this.wlanControllerLayoutChangedSub) {
			this.wlanControllerLayoutChangedSub.unsubscribe();
		}
		if (this.wirelessApLayoutChangedSub) {
			this.wirelessApLayoutChangedSub.unsubscribe();
		}
		if (this.printerLayoutChangedSub) {
			this.printerLayoutChangedSub.unsubscribe();
		}

		if (this.virtualHostLayoutChangedSub) {
			this.virtualHostLayoutChangedSub.unsubscribe();
		}
		if (this.virtualMachineLayoutChangedSub) {
			this.virtualMachineLayoutChangedSub.unsubscribe();
		}
		if (this.windowsServerLayoutChangedSub) {
			this.windowsServerLayoutChangedSub.unsubscribe();
		}
		if (this.windowsLayoutChangedSub) {
			this.windowsLayoutChangedSub.unsubscribe();
		}
		if (this.appleLayoutChangedSub) {
			this.appleLayoutChangedSub.unsubscribe();
		}
		if (this.ipPhoneManagerLayoutChangedSub) {
			this.ipPhoneManagerLayoutChangedSub.unsubscribe();
		}
		if (this.ipPhoneLayoutChangedSub) {
			this.ipPhoneLayoutChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.apiKey = undefined;

		this.defaultLayout = undefined;
		this.firewallLayout = undefined;
		this.routerLayout = undefined;
		this.switchLayout = undefined;
		this.wlanControllerLayout = undefined;
		this.wirelessApLayout = undefined;
		this.printerLayout = undefined;

		this.virtualHostLayout = undefined;
		this.virtualMachineLayout = undefined;
		this.windowsServerLayout = undefined;
		this.windowsLayout = undefined;
		this.appleLayout = undefined;
		this.ipPhoneManagerLayout = undefined;
		this.ipPhoneLayout = undefined;

		let enabled = true;
		let serverURL = "";
		let apiKey = "";

		// Layouts
		let defaultLayout = "";

		let firewallLayout = "";
		let routerLayout = "";
		let switchLayout = "";
		let wlanControllerLayout = "";
		let wirelessApLayout = "";
		let printerLayout = "";

		let virtualHostLayout = "";
		let virtualMachineLayout = "";
		let windowsServerLayout = "";
		let windowsLayout = "";
		let appleLayout = "";
		let ipPhoneManagerLayout = "";
		let ipPhoneLayout = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			apiKey = (this.inputSettings.HasApiKey ? this.hidePassword(this.inputSettings.ApiKey) : "");

			defaultLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.NetworkDevice.Name);

			firewallLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Firewall.Name);
			routerLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Router.Name);
			switchLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Switch.Name);
			wlanControllerLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.WirelessController.Name);
			wirelessApLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.WirelessAp.Name);
			printerLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Printer.Name);

			virtualHostLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.VirtualHost.Name);
			virtualMachineLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.VirtualMachine.Name);
			windowsServerLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.WindowsServer.Name);
			windowsLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Windows.Name);
			appleLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.Apple.Name);
			ipPhoneManagerLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.IpPhoneManager.Name);
			ipPhoneLayout = this.inputSettings.CategoryMap.GetCategory(DeviceCategory.IpPhone.Name);
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.apiKey = new UntypedFormControl(apiKey);

		this.defaultLayout = new UntypedFormControl(defaultLayout);

		this.firewallLayout = new UntypedFormControl(firewallLayout);
		this.routerLayout = new UntypedFormControl(routerLayout);
		this.switchLayout = new UntypedFormControl(switchLayout);
		this.wlanControllerLayout = new UntypedFormControl(wlanControllerLayout);
		this.wirelessApLayout = new UntypedFormControl(wirelessApLayout);
		this.printerLayout = new UntypedFormControl(printerLayout);

		this.virtualHostLayout = new UntypedFormControl(virtualHostLayout);
		this.virtualMachineLayout = new UntypedFormControl(virtualMachineLayout);
		this.windowsServerLayout = new UntypedFormControl(windowsServerLayout);
		this.windowsLayout = new UntypedFormControl(windowsLayout);
		this.appleLayout = new UntypedFormControl(appleLayout);
		this.ipPhoneManagerLayout = new UntypedFormControl(ipPhoneManagerLayout);
		this.ipPhoneLayout = new UntypedFormControl(ipPhoneLayout);

		this.apiKeyChanged = false;
		this.apiKeyChangedSub = this.apiKey.valueChanges.subscribe(() => {
			this.apiKeyChanged = true;
		});

		this.layoutChanged = false;
		this.defaultLayoutChangedSub = this.defaultLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.firewallLayoutChangedSub = this.firewallLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.routerLayoutChangedSub = this.routerLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.switchLayoutChangedSub = this.switchLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.wlanControllerLayoutChangedSub = this.wlanControllerLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.wirelessApLayoutChangedSub = this.wirelessApLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.printerLayoutChangedSub = this.printerLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});

		this.virtualHostLayoutChangedSub = this.virtualHostLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.virtualMachineLayoutChangedSub = this.virtualMachineLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.windowsServerLayoutChangedSub = this.windowsServerLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.windowsLayoutChangedSub = this.windowsLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.appleLayoutChangedSub = this.appleLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.ipPhoneManagerLayoutChangedSub = this.ipPhoneManagerLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});
		this.ipPhoneLayoutChangedSub = this.ipPhoneLayout.valueChanges.subscribe(() => {
			this.layoutChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			apiKey: this.apiKey,

			defaultLayout: this.defaultLayout,

			firewallLayout: this.firewallLayout,
			routerLayout: this.routerLayout,
			switchLayout: this.switchLayout,
			wlanControllerLayout: this.wlanControllerLayout,
			wirelessApLayout: this.wirelessApLayout,
			printerLayout: this.printerLayout,

			virtualHostLayout: this.virtualHostLayout,
			virtualMachineLayout: this.virtualMachineLayout,
			windowsServerLayout: this.windowsServerLayout,
			windowsLayout: this.windowsLayout,
			appleLayout: this.appleLayout,
			ipPhoneManagerLayout: this.ipPhoneManagerLayout,
			ipPhoneLayout: this.ipPhoneLayout,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return true;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved Hudu default settings");
				this.form.markAsPristine();

				if (this.inputSettings) {
					this.inputSettings.Copy(integrationSettings);
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save Hudu default settings");
			});
	}

	public get enableTestHuduButton(): boolean {
		return (!!this.serverURL && !!this.serverURL.value
			&& !!this.apiKey && !!this.apiKey.value);
	}

	public testConnection(): void {
		let apiKey = "";
		if (this.apiKeyChanged) {
			apiKey = StringUtils.ObfuscateString2(this.apiKey.value);
		}
		else {
			apiKey = this.inputSettings.ApiKey;
		}
		this.configService.testHuduConnection(this.serverURL.value, apiKey).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Hudu");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Hudu");
			});
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiKeyChanged) {
			this.apiKeyChanged = true;

			this.apiKey.setValue('');
			this.apiKey.markAsDirty();
		}
	}

	public onLayoutKeyDownHandler(event: any): void {
		if (!this.layoutChanged) {
			this.layoutChanged = true;

			this.defaultLayout.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.Hudu);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		if (this.apiKeyChanged) {
			outputIntegration.ApiKey = StringUtils.ObfuscateString2(this.apiKey.value);
		}
		else {
			outputIntegration.ApiKey = this.inputSettings.ApiKey;
		}

		outputIntegration.CategoryMap.Clear();

		outputIntegration.CategoryMap.Add(DeviceCategory.NetworkDevice.Name, this.defaultLayout.value);

		outputIntegration.CategoryMap.Add(DeviceCategory.Firewall.Name, this.firewallLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.Router.Name, this.routerLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.Switch.Name, this.switchLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.WirelessController.Name, this.wlanControllerLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.WirelessAp.Name, this.wirelessApLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.Printer.Name, this.printerLayout.value);

		outputIntegration.CategoryMap.Add(DeviceCategory.VirtualHost.Name, this.virtualHostLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.VirtualMachine.Name, this.virtualMachineLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.WindowsServer.Name, this.windowsServerLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.Windows.Name, this.windowsLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.Apple.Name, this.appleLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.IpPhoneManager.Name, this.ipPhoneManagerLayout.value);
		outputIntegration.CategoryMap.Add(DeviceCategory.IpPhone.Name, this.ipPhoneLayout.value);

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.apiKey.value);
			if (b1) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
